import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Form } from 'react-bootstrap';
import { FaBold, FaItalic, FaListUl, FaListOl, FaSubscript, FaSuperscript, FaSyncAlt } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import './Resume.css';
import Notes from "../../Pages/Images/WhatsApp Image 2025-06-12 at 5.33.20 PM.webp";
import axios from 'axios';
import Swal from 'sweetalert2';
import BaseUrl from '../../ApiServices/BaseUrl'
import AgencyNavbar from '../../Pages/Navbar/AgencyNavbar';

const Resume = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const userId = location.state?.userId || user.id || '';

  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [wordCount, setWordCount] = useState(0);

  const editorRef = useRef(null);
  const [htmlContent, setHtmlContent] = useState('');

  // Auto-fill state
  const [profileData, setProfileData] = useState(null);
  const [currentTemplateIndex, setCurrentTemplateIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [shouldAutoFill, setShouldAutoFill] = useState(false);

  // Helper to safely parse JSON arrays from DB
  const safeJsonParse = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (e) {
      if (typeof value === 'string') return [value];
      return [];
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchJobSeekerData = async () => {
      try {
        const response = await axios.get(`${BaseUrl}/api/job-seeker/${userId}`);
        if (response.data) {
          if (response.data.resume_description) {
            const savedDescription = response.data.resume_description || '';
            setDescription(savedDescription);
            updateWordCount(savedDescription);

            if (editorRef.current) {
              editorRef.current.innerHTML = savedDescription || '';
            }

            setIsSaved(true);
          }

          // Auto-generation profile data setup
          setShouldAutoFill(true);
          const data = response.data;
          setProfileData({
            first_name: data.first_name || '',
            last_name: data.last_name || '',
            age: data.age || '',
            gender: data.gender && data.gender !== 'Not specified' ? data.gender : '',
            nationality: data.nationality || '',
            marital_status: data.marital_status && data.marital_status !== 'Not specified' ? data.marital_status : '',
            current_country: data.current_country || '',
            job_position: safeJsonParse(data.job_position),
            category: data.category || '',
            total_work_experience: data.total_work_experience || '',
            personality: safeJsonParse(data.personality),
            required_skills: safeJsonParse(data.required_skills),
            language_skills: safeJsonParse(data.language_skills),
            experience: data.experience ? (typeof data.experience === 'string' ? (() => { try { return JSON.parse(data.experience); } catch (e) { return []; } })() : (Array.isArray(data.experience) ? data.experience : [])) : [],
            religion: data.religion || '',
            preferred_start_date: data.preferred_start_date || '',
            has_prometric_certificate: data.has_prometric_certificate,
            has_dataflow_certificate: data.has_dataflow_certificate,
          });
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error('Error fetching job seeker data:', err);
          setError(err.response?.data?.message || 'Failed to fetch resume description');
        }
      }
    };

    if (userId) {
      fetchJobSeekerData();
    }
  }, [userId]);

  const extractPlainText = (html) => {
    if (!html) return '';

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    return tempDiv.textContent || tempDiv.innerText || '';
  };

  const updateWordCount = (text) => {
    const cleanText = text.includes('<') ? extractPlainText(text) : text;
    const words = cleanText.trim() ? cleanText.trim().split(/\s+/) : [];
    setWordCount(words.length);
  };

  const handleEditorChange = () => {
    if (editorRef.current) {
      const htmlContent = editorRef.current.innerHTML;
      const plainTextContent = extractPlainText(htmlContent);

      setHtmlContent(htmlContent);
      setDescription(plainTextContent);
      updateWordCount(plainTextContent);
      setIsSaved(false);
    }
  };

  const applyFormat = (command, value = null) => {
    if (value) {
      document.execCommand(command, false, value);
    } else {
      document.execCommand(command, false, null);
    }
    handleEditorChange();
  };

  // ===== AUTO-FILL TEMPLATE ENGINE =====

  const getFormattedName = () => {
    if (!profileData) return '';
    const { first_name, last_name } = profileData;
    if (first_name && last_name) return `${first_name} ${last_name}`;
    return first_name || last_name || '';
  };

  const getJobPositionText = () => {
    if (!profileData || !profileData.job_position.length) return '';
    return profileData.job_position.join(' / ');
  };

  const getPersonalityText = () => {
    if (!profileData || !profileData.personality.length) return '';
    const traits = profileData.personality;
    if (traits.length === 1) return traits[0].toLowerCase();
    if (traits.length === 2) return `${traits[0].toLowerCase()} and ${traits[1].toLowerCase()}`;
    return traits.slice(0, -1).map(t => t.toLowerCase()).join(', ') + ', and ' + traits[traits.length - 1].toLowerCase();
  };

  const getLanguagesText = () => {
    if (!profileData || !profileData.language_skills.length) return '';
    const langs = profileData.language_skills;
    if (langs.length === 1) return langs[0];
    if (langs.length === 2) return `${langs[0]} and ${langs[1]}`;
    return langs.slice(0, -1).join(', ') + ', and ' + langs[langs.length - 1];
  };

  const getSkillsText = () => {
    if (!profileData || !profileData.required_skills.length) return '';
    const skills = profileData.required_skills;
    if (skills.length === 1) return skills[0];
    if (skills.length === 2) return `${skills[0]} and ${skills[1]}`;
    return skills.slice(0, -1).join(', ') + ', and ' + skills[skills.length - 1];
  };

  const getCountriesWorked = () => {
    if (!profileData || !profileData.experience || !profileData.experience.length) return '';
    const allCountries = new Set();
    profileData.experience.forEach(exp => {
      if (exp.country && Array.isArray(exp.country)) {
        exp.country.forEach(c => allCountries.add(c));
      }
    });
    const countries = [...allCountries];
    if (countries.length === 0) return '';
    if (countries.length === 1) return countries[0];
    if (countries.length === 2) return `${countries[0]} and ${countries[1]}`;
    return countries.slice(0, -1).join(', ') + ', and ' + countries[countries.length - 1];
  };

  const getGenderPronoun = () => {
    if (!profileData || !profileData.gender) return 'their';
    if (profileData.gender === 'Male') return 'his';
    if (profileData.gender === 'Female') return 'her';
    return 'their';
  };

  const getStartDateText = () => {
    if (!profileData || !profileData.preferred_start_date) return '';
    if (profileData.preferred_start_date === 'Immediate') return 'immediately';
    try {
      const date = new Date(profileData.preferred_start_date);
      if (isNaN(date.getTime())) return '';
      return `from ${date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
    } catch (e) {
      return '';
    }
  };

  // Helper: detect if job position is nurse-related
  const isNursePosition = () => {
    const nurseKeywords = ['nurse', 'nursing'];
    const positions = profileData?.job_position || [];
    return positions.some(p => nurseKeywords.some(k => p.toLowerCase().includes(k)));
  };

  // Helper: build certificate sentence for nurses
  const getCertificateText = () => {
    if (!isNursePosition()) return '';
    const hasPrometric = profileData?.has_prometric_certificate === true || profileData?.has_prometric_certificate === 1;
    const hasDataflow = profileData?.has_dataflow_certificate === true || profileData?.has_dataflow_certificate === 1;
    const certs = [];
    if (hasPrometric) certs.push('**Prometric Certificate**');
    if (hasDataflow) certs.push('**Dataflow Certificate**');
    if (certs.length === 0) return '';
    return `I hold a ${certs.join(' and ')}.`;
  };

  // Template 1: Simple Introduction
  const generateTemplate1 = () => {
    const parts = [];
    const name = getFormattedName();
    const age = profileData?.age;
    const nationality = profileData?.nationality;
    const jobPosition = getJobPositionText();
    const experience = profileData?.total_work_experience;
    const personality = getPersonalityText();
    const languages = getLanguagesText();
    const skills = getSkillsText();
    const countriesWorked = getCountriesWorked();
    const currentCountry = profileData?.current_country;
    const startDate = getStartDateText();
    const certText = getCertificateText();

    if (name) {
      let intro = `My name is ${name}`;
      if (age) intro += `, I am ${age} years old`;
      if (nationality) intro += ` and I am from ${nationality}`;
      intro += '.';
      parts.push(intro);
    }

    if (jobPosition) {
      if (experience && experience !== 'Fresher') {
        parts.push(`I work as a ${jobPosition} and have ${experience} of experience in this field.`);
      } else {
        parts.push(`I am looking to start my career as a ${jobPosition}.`);
      }
    }

    if (certText) parts.push(certText);

    if (personality) {
      parts.push(`I am ${personality} and I take my work seriously.`);
    }

    if (skills) {
      parts.push(`My skills include ${skills}.`);
    }

    if (countriesWorked) {
      parts.push(`I have work experience in ${countriesWorked}.`);
    }

    if (languages) {
      parts.push(`I can speak ${languages}.`);
    }

    if (currentCountry) {
      parts.push(`I am currently in ${currentCountry}.`);
    }

    parts.push('I am looking for a good opportunity where I can work hard and grow.');

    if (startDate) {
      parts.push(`I can start ${startDate}.`);
    }

    return parts.join(' ');
  };

  // Template 2: Skills Focused
  const generateTemplate2 = () => {
    const parts = [];
    const jobPosition = getJobPositionText();
    const experience = profileData?.total_work_experience;
    const skills = getSkillsText();
    const personality = getPersonalityText();
    const languages = getLanguagesText();
    const currentCountry = profileData?.current_country;
    const category = profileData?.category;
    const countriesWorked = getCountriesWorked();
    const name = getFormattedName();
    const nationality = profileData?.nationality;
    const age = profileData?.age;
    const startDate = getStartDateText();
    const certText = getCertificateText();

    if (jobPosition) {
      if (experience && experience !== 'Fresher') {
        parts.push(`I am a ${jobPosition} with ${experience} of work experience in the ${category || 'service'} field.`);
      } else {
        parts.push(`I am a ${jobPosition} who is new to the field but eager to learn and work hard.`);
      }
    }

    if (certText) parts.push(certText);

    if (skills) {
      parts.push(`I am skilled in ${skills}.`);
    }

    if (personality) {
      parts.push(`People know me as someone who is ${personality}.`);
    }

    if (countriesWorked) {
      parts.push(`I have worked in ${countriesWorked} before.`);
    }

    if (languages) {
      parts.push(`I can communicate in ${languages}.`);
    }

    if (currentCountry) {
      parts.push(`Right now I am based in ${currentCountry} and I am available to start work.`);
    }

    if (name) {
      parts.push(`I, ${name}, am ready to give my best to any employer who hires me.`);
    }

    if (startDate) {
      parts.push(`I am available ${startDate}.`);
    }

    return parts.join(' ');
  };

  // Template 3: Experience Focused
  const generateTemplate3 = () => {
    const parts = [];
    const name = getFormattedName();
    const jobPosition = getJobPositionText();
    const experience = profileData?.total_work_experience;
    const skills = getSkillsText();
    const personality = getPersonalityText();
    const languages = getLanguagesText();
    const countriesWorked = getCountriesWorked();
    const nationality = profileData?.nationality;
    const age = profileData?.age;
    const currentCountry = profileData?.current_country;
    const startDate = getStartDateText();
    const certText = getCertificateText();

    if (jobPosition) {
      if (experience && experience !== 'Fresher') {
        parts.push(`I have been working as a ${jobPosition} for ${experience} and I have learned a lot during this time.`);
      } else {
        parts.push(`I am a ${jobPosition} who is just starting out and I am very motivated to do well.`);
      }
    }

    if (certText) parts.push(certText);

    if (countriesWorked) {
      parts.push(`I have had the chance to work in ${countriesWorked}, which helped me understand different work cultures.`);
    }

    if (skills) {
      parts.push(`Some of my key skills are ${skills}.`);
    }

    if (personality) {
      parts.push(`By nature, I am ${personality}, which helps me work well with different kinds of people.`);
    }

    if (languages) {
      parts.push(`I speak ${languages}.`);
    }

    if (name && nationality) {
      parts.push(`My name is ${name}${nationality ? ` and I am from ${nationality}` : ''}${age ? `, ${age} years old` : ''}.`);
    }

    if (currentCountry) {
      parts.push(`I am currently in ${currentCountry} and looking for new work.`);
    }

    if (startDate) {
      parts.push(`I can join ${startDate}.`);
    }

    return parts.join(' ');
  };

  // Template 4: Personal Introduction
  const generateTemplate4 = () => {
    const parts = [];
    const name = getFormattedName();
    const jobPosition = getJobPositionText();
    const experience = profileData?.total_work_experience;
    const skills = getSkillsText();
    const personality = getPersonalityText();
    const languages = getLanguagesText();
    const nationality = profileData?.nationality;
    const age = profileData?.age;
    const maritalStatus = profileData?.marital_status;
    const countriesWorked = getCountriesWorked();
    const currentCountry = profileData?.current_country;
    const startDate = getStartDateText();
    const certText = getCertificateText();

    if (name && nationality) {
      let intro = `I am ${name} from ${nationality}`;
      if (age) intro += `, ${age} years old`;
      if (maritalStatus) intro += `, ${maritalStatus.toLowerCase()}`;
      intro += '.';
      parts.push(intro);
    }

    if (jobPosition) {
      if (experience && experience !== 'Fresher') {
        parts.push(`I work as a ${jobPosition} and have ${experience} of hands-on experience.`);
      } else {
        parts.push(`I am looking for my first job as a ${jobPosition}.`);
      }
    }

    if (certText) parts.push(certText);

    if (personality) {
      parts.push(`I am ${personality} and I always try to do my best at work.`);
    }

    if (skills) {
      parts.push(`I am good at ${skills}.`);
    }

    if (countriesWorked) {
      parts.push(`I have worked in ${countriesWorked} in the past.`);
    }

    if (languages) {
      parts.push(`I speak ${languages}.`);
    }

    if (currentCountry) {
      parts.push(`I am currently in ${currentCountry}.`);
    }

    parts.push('I am honest, hardworking, and committed to doing a good job.');

    if (startDate) {
      parts.push(`I am available to start ${startDate}.`);
    }

    return parts.join(' ');
  };

  // Template 5: Short Summary
  const generateTemplate5 = () => {
    const parts = [];
    const name = getFormattedName();
    const jobPosition = getJobPositionText();
    const experience = profileData?.total_work_experience;
    const skills = getSkillsText();
    const personality = getPersonalityText();
    const languages = getLanguagesText();
    const nationality = profileData?.nationality;
    const countriesWorked = getCountriesWorked();
    const currentCountry = profileData?.current_country;
    const age = profileData?.age;
    const startDate = getStartDateText();
    const certText = getCertificateText();

    if (name && jobPosition) {
      let summary = `${name} is a ${jobPosition}`;
      if (experience && experience !== 'Fresher') summary += ` with ${experience} of experience`;
      if (nationality) summary += `, originally from ${nationality}`;
      if (age) summary += `, ${age} years old`;
      summary += '.';
      parts.push(summary);
    }

    if (certText) parts.push(certText);

    if (countriesWorked) {
      parts.push(`Work experience from ${countriesWorked}.`);
    }

    if (languages) {
      parts.push(`Can speak ${languages}.`);
    }

    if (skills) {
      parts.push(`Skills: ${skills}.`);
    }

    if (personality) {
      parts.push(`Known to be ${personality}.`);
    }

    if (currentCountry) {
      parts.push(`Currently in ${currentCountry} and available to work.`);
    }

    parts.push('Looking for a good employer to work with and grow together.');

    if (startDate) {
      parts.push(`Can start ${startDate}.`);
    }

    return parts.join(' ');
  };

  const templates = [generateTemplate1, generateTemplate2, generateTemplate3, generateTemplate4, generateTemplate5];

  // Auto-fill on page load when profileData is ready and no saved description
  useEffect(() => {
    if (shouldAutoFill && profileData) {
      // For editing agency, we DO NOT auto overwrite an already saved description!
      // But we CAN if it was empty. The seeker version always overwrites if shouldn't auto-fill is true.
      // We will only do it if the main description is empty, to be safe.
      if (!description.trim()) {
        const generatedText = templates[0]();
        if (editorRef.current) {
          editorRef.current.innerHTML = generatedText;
        }
        setDescription(generatedText);
        updateWordCount(generatedText);
      }
      setShouldAutoFill(false);
    }
  }, [shouldAutoFill, profileData]);

  const handleChangeDescription = () => {
    if (!profileData) return;

    const nextIndex = (currentTemplateIndex + 1) % templates.length;
    setCurrentTemplateIndex(nextIndex);
    setIsGenerating(true);

    const generatedText = templates[nextIndex]();

    if (editorRef.current) {
      editorRef.current.innerHTML = generatedText;
    }
    setDescription(generatedText);
    updateWordCount(generatedText);
    setIsSaved(false);

    setTimeout(() => setIsGenerating(false), 300);
  };

  // ===== END AUTO-FILL =====

  const saveData = async () => {
    setIsLoading(true);
    setError(null);

    Swal.fire({
      title: 'Saving...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const dataToSend = {
        user_id: userId,
        resume_description: description
      };

      const response = await axios.post(`${BaseUrl}/api/job-seeker`, dataToSend);

      await Swal.fire({
        title: 'Success!',
        text: 'Your resume description has been saved successfully.',
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });

      setIsSaved(true);
      return response.data;
    } catch (err) {
      console.error('Error saving data:', err);

      await Swal.fire({
        title: 'Error!',
        text: err.response?.data?.message || 'Failed to save resume description. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });

      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const validateFormData = () => {
    if (!description.trim()) {
      setError('Please enter your resume description');
      return false;
    }

    if (wordCount < 60) {
      setError(`Your description must be at least 60 words (currently ${wordCount} words)`);
      return false;
    }

    setError(null);
    return true;
  };

  const handleSave = async () => {
    if (!validateFormData()) return;

    try {
      await saveData();
    } catch (err) {
      // Error already handled in saveData
    }
  };

  const handleSaveNext = async () => {
    if (!validateFormData()) return;

    try {
      await saveData();
      window.scrollTo(0, 0);
      navigate('/a-uploadvideo', { state: { userId } });
    } catch (err) {
      // Error already handled in saveData
    }
  };

  const handleBack = () => {
    navigate('/a-education', { state: { userId } });
  };

  return (
    <div style={{ marginTop: "55px" }}>
      <AgencyNavbar />
      <Container className="resume-description-container my-4" style={{ marginTop: "84px" }}>
        {/* Fixed Header */}
        <div className="resume-header d-flex align-items-center justify-content-center">
          <div className="resume-icon me-2">
            <img
              src={Notes}
              alt="notes-icon"
              className="Notes"
            />
          </div>
          <h1 className="resume-title mb-0">Provide details about you</h1>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <h5 className="resume-label mb-2 mt-3">
          <span className="text-danger">*</span>
        </h5>

        <div className="resume-editor-container p-3 mx-auto">
          {/* Rich Text Editor Toolbar */}
          <div className="editor-toolbar mb-3 d-flex gap-2 flex-wrap">
            <Button
              variant="light"
              className="editor-btn fw-bold"
              onClick={() => applyFormat('bold')}
              title="Bold"
            >
              <FaBold />
            </Button>
            <Button
              variant="light"
              className="editor-btn fst-italic"
              onClick={() => applyFormat('italic')}
              title="Italic"
            >
              <FaItalic />
            </Button>
            <Button
              variant="light"
              className="editor-btn"
              onClick={() => applyFormat('underline')}
              title="Underline"
            >
              U
            </Button>
            <Button
              variant="light"
              className="editor-btn"
              onClick={() => applyFormat('insertUnorderedList')}
              title="Bullet List"
            >
              <FaListUl />
            </Button>
            <Button
              variant="light"
              className="editor-btn"
              onClick={() => applyFormat('insertOrderedList')}
              title="Numbered List"
            >
              <FaListOl />
            </Button>
            <Button
              variant="light"
              className="editor-btn"
              onClick={() => applyFormat('subscript')}
              title="Subscript"
            >
              <FaSubscript />
            </Button>
            <Button
              variant="light"
              className="editor-btn"
              onClick={() => applyFormat('superscript')}
              title="Superscript"
            >
              <FaSuperscript />
            </Button>
            <div className="d-flex border rounded">
              <Button
                variant="light"
                className="editor-btn border-0"
                onClick={() => applyFormat('fontSize', 1)}
                title="Small Font"
              >
                A<small>°</small>
              </Button>
              <Button
                variant="light"
                className="editor-btn border-0"
                onClick={() => applyFormat('fontSize', 3)}
                title="Normal Font"
              >
                A
              </Button>
              <Button
                variant="light"
                className="editor-btn border-0"
                onClick={() => applyFormat('fontSize', 5)}
                title="Large Font"
              >
                A<sup>°</sup>
              </Button>
            </div>

            {/* Change Description Button - right side */}
            <div className="auto-generate-toolbar ms-auto">
              <Button
                className="change-desc-btn d-flex align-items-center gap-1"
                onClick={handleChangeDescription}
                disabled={isGenerating || !profileData}
                title="Generate a different description style"
                size="sm"
              >
                <FaSyncAlt className={isGenerating ? 'spin-icon' : ''} />
                Change Description
              </Button>
            </div>
          </div>

          {/* Rich Text Editor Content Area */}
          <div
            ref={editorRef}
            contentEditable
            className="editor-content p-3"
            style={{
              minHeight: '250px',
              lineHeight: '1.5',
              backgroundColor: 'white',
              border: '1px solid #ced4da',
              borderRadius: '0px',
              overflowY: 'auto'
            }}
            onInput={handleEditorChange}
            placeholder="Enter your resume description here (minimum 60 words)..."
          />

          <div className={`word-count mt-2 text-end ${wordCount < 60 ? 'text-danger' : 'text-success'}`}>
            Word count: {wordCount} / 60
          </div>

        </div>

        {/* Navigation Buttons */}
        <div className="additional-section mb-4 mt-4">
          <div className="d-flex justify-content-between gap-2" style={{ width: "100%" }}>
            <button
              className="nav-btn"
              style={{ flex: 1 }}
              onClick={handleBack}
            >
              Back
            </button>
            <button
              className="nav-btn"
              style={{ flex: 1 }}
              onClick={handleSaveNext}
              disabled={isLoading || wordCount < 60}
            >
              Next
            </button>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Resume;
